import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import API from "@/utils/api";

interface CorrectRateDetail {
  categoryName?: string;
  correctRate?: number;
}

interface CorrectRateData {
  dictationCorrectRate?: {
    total?: number;
    detail?: CorrectRateDetail[];
  };
  wordCorrectRate?: {
    total?: number;
    detail?: CorrectRateDetail[];
  };
}

const CorrectRate = () => {
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeTab, setActiveTab] = useState<"dictation" | "word">("dictation");
  const [sortOrder, setSortOrder] = useState<"desc" | "asc">("desc");

  // Refs for tab switch
  const dictationBtnRef = useRef<HTMLButtonElement>(null);
  const wordBtnRef = useRef<HTMLButtonElement>(null);
  const sliderRef = useRef<HTMLDivElement>(null);

  // React Query for correct rate data
  const { data: correctRateData, isLoading: isLoadingCorrectRate } = useQuery<CorrectRateData>({
    queryKey: ["progress-correct-rate"],
    queryFn: async () => {
      const response = await API.get("progress/correct-rate");
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      return response.data.data as CorrectRateData;
    },
    staleTime: 30000,
    refetchOnWindowFocus: true,
  });

  useEffect(() => {
    function handleScroll() {
      const scrollY = window.scrollY;
      setIsScrolled(scrollY > 100);
    }

    window.addEventListener("scroll", handleScroll);
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // Move slider for tab switch
  const moveSlider = (target: HTMLElement | null) => {
    if (!sliderRef.current || !target) return;
    const extraWidth = 20;
    sliderRef.current.style.width = `${target.offsetWidth + extraWidth}px`;
    sliderRef.current.style.left = `${target.offsetLeft - extraWidth / 2}px`;
  };

  const handleTabChange = (tab: "dictation" | "word") => {
    setActiveTab(tab);
    if (tab === "dictation") moveSlider(dictationBtnRef.current);
    if (tab === "word") moveSlider(wordBtnRef.current);
  };

  useEffect(() => {
    if (activeTab === "dictation") moveSlider(dictationBtnRef.current);
    if (activeTab === "word") moveSlider(wordBtnRef.current);
  }, [activeTab]);

  // Get and sort data based on active tab and sort order
  const getSortedData = (): CorrectRateDetail[] => {
    const data =
      activeTab === "dictation"
        ? (correctRateData?.dictationCorrectRate?.detail ?? [])
        : (correctRateData?.wordCorrectRate?.detail ?? []);

    // Create a copy to avoid mutating original data
    const sortedData = [...data].sort((a, b) => {
      const rateA = a.correctRate ?? 0;
      const rateB = b.correctRate ?? 0;
      return sortOrder === "desc" ? rateB - rateA : rateA - rateB;
    });

    return sortedData;
  };

  const handleSortClick = () => {
    setSortOrder((prev) => (prev === "desc" ? "asc" : "desc"));
  };

  return (
    <>
      <Helmet>
        <title>正答率</title>
      </Helmet>
      <div className="min-h-screen bg-[#e0f2fe]">
        <nav
          className={`${isScrolled ? "fixed top-0 left-0 right-0 z-50 scrolled bg-gradient-custom" : "relative"} bg-gradient-custom transition-all duration-300`}
        >
          <div className="mx-auto w-full px-4 max-w-5xl relative">
            <div className="flex items-center justify-between w-full px-4 py-3 relative">
              <button
                className="flex items-center justify-center p-2 cursor-pointer border-none bg-transparent"
                type="button"
                onClick={() => {
                  void navigate(-1);
                }}
              >
                <img src="/images/arrow-left.svg" alt="Menu" className="w-6 h-6" />
              </button>
              <span className="absolute left-1/2 -translate-x-1/2 text-[18px] font-bold text-white">
                正答率
              </span>
            </div>
          </div>
        </nav>

        <div
          className={`pb-5 pt-5 min-h-screen ${isScrolled ? "pt-16 lg:pt-20" : "pt-16 lg:pt-0"}`}
        >
          <div className="mx-auto w-full px-4 max-w-5xl">
            {/* Tab Switch */}
            <div className="mb-4 mt-5">
              <div className="flex bg-white rounded-[30px] w-full relative overflow-visible shadow-[0_2px_5px_rgba(0,0,0,0.1)] transition-all duration-300 hover:shadow-[0_4px_8px_rgba(0,0,0,0.15)] font-bold">
                <div
                  ref={sliderRef}
                  className="absolute bg-gradient-to-r from-gradient-start to-gradient-end rounded-[25px] transition-all duration-300 z-[1] h-[calc(100%+6px)] -top-[3px] shadow-[0px_2px_8px_rgba(42,81,214,0.4)] left-[5px] w-[10px]"
                ></div>
                <button
                  ref={dictationBtnRef}
                  className={`flex-1 bg-transparent border-none text-[#888] py-2 px-[15px] rounded-[25px] mx-1.5 cursor-pointer text-sm font-medium relative z-[2] transition-all duration-300 ${activeTab === "dictation" ? "text-white" : ""}`}
                  onClick={() => handleTabChange("dictation")}
                >
                  ディクテーション
                </button>
                <button
                  ref={wordBtnRef}
                  className={`flex-1 bg-transparent border-none text-[#888] py-2 px-[15px] rounded-[25px] mx-1.5 cursor-pointer text-sm font-medium relative z-[2] transition-all duration-300 ${activeTab === "word" ? "text-white" : ""}`}
                  onClick={() => handleTabChange("word")}
                >
                  単語・熟語
                </button>
              </div>
            </div>

            {/* Accuracy Rate Card */}
            <div className="bg-white rounded-2xl p-5 shadow-[0_4px_8px_rgba(0,0,0,0.1)] mb-4">
              <h3 className="text-base font-bold mb-4 text-[#333]">正答率</h3>
              {isLoadingCorrectRate ? (
                <div className="text-center py-4">
                  <div className="text-3xl font-bold mb-4">...</div>
                  <div className="space-y-2">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <div key={i} className="h-10 bg-gray-100 animate-pulse rounded"></div>
                    ))}
                  </div>
                </div>
              ) : (
                <>
                  <div className="text-3xl font-bold mb-4 text-center text-[#1e3a8a]">
                    {activeTab === "dictation"
                      ? `${Math.round(correctRateData?.dictationCorrectRate?.total ?? 0)}%`
                      : `${Math.round(correctRateData?.wordCorrectRate?.total ?? 0)}%`}
                  </div>
                  <div className="flex justify-between select-none">
                    <div className="text-primary text-[#666] mb-1 font-bold">カテゴリ別</div>
                    <div
                      className="text-primary text-[#666] mb-1 font-bold flex items-center gap-1 cursor-pointer hover:opacity-80 transition-opacity"
                      onClick={handleSortClick}
                    >
                      {sortOrder === "desc" ? "高い順" : "低い順"}
                      <svg
                        width="9"
                        height="5"
                        viewBox="0 0 9 5"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        style={{
                          transform: sortOrder === "asc" ? "rotate(180deg)" : "rotate(0deg)",
                          transition: "transform 0.3s ease",
                        }}
                      >
                        <path d="M0 4.375L4.49206 0L9 4.375" fill="#2585D2" />
                      </svg>
                    </div>
                  </div>
                  <ul className="list-none p-0 m-0 space-y-2">
                    {getSortedData().map((item, index) => (
                      <li key={index} className="flex items-center py-2">
                        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-[#2585D2] text-white font-bold mr-3 text-sm">
                          {index + 1}
                        </div>
                        <div className="flex items-center gap-2 w-full">
                          <div className="px-4 py-1 w-full bg-[#ECECEC] rounded-full overflow-hidden relative">
                            <span className="text-lg font-bold relative z-10">
                              {item.categoryName ?? ""}
                            </span>
                            <div
                              className="absolute inset-0 h-full bg-[#E0F2FE] rounded-full"
                              style={{
                                width: `${Math.min(100, Math.max(0, item.correctRate ?? 0))}%`,
                              }}
                            ></div>
                          </div>
                          <span className="text-lg font-bold min-w-[50px] font-figtree text-right">
                            {Math.round(item.correctRate ?? 0)}%
                          </span>
                        </div>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CorrectRate;
